import { Component, useEffect, useRef, useState } from "react";

import {
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  RefreshCw,
  TriangleAlert,
  X,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

import type { LucideIcon } from "lucide-react";
import type { ErrorInfo, ReactNode } from "react";

const DETAILS_PANEL_ID = "error-boundary-details";

type CopyStatus = "idle" | "copied" | "failed";

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  inline?: boolean;
};

type State = {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
};

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentWillUnmount() {
    window.removeEventListener("popstate", this.reset);
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error boundary caught an error:", error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack ?? undefined });
    // Only a browser back/forward can reach a boundary that has replaced the router.
    window.addEventListener("popstate", this.reset);
  }

  private reset = () => {
    if (!this.state.hasError) return;
    window.removeEventListener("popstate", this.reset);
    this.setState({
      hasError: false,
      error: undefined,
      componentStack: undefined,
    });
  };

  public override render() {
    const { children, fallback, inline } = this.props;

    if (!this.state.hasError) return children;

    return (
      fallback ?? (
        <ErrorFallback
          error={this.state.error}
          componentStack={this.state.componentStack}
          inline={inline}
        />
      )
    );
  }
}

type ErrorFallbackProps = {
  error?: Error;
  componentStack?: string;
  inline?: boolean;
};

const ErrorFallback = ({
  error,
  componentStack,
  inline,
}: ErrorFallbackProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const card = useRef<HTMLDivElement>(null);
  /*
  history.length counts the whole tab, cross-origin entries included. React Router's own
  index is the only reliable read of "there is an in-app page behind this one".
  */
  const canGoBack =
    !inline && ((window.history.state?.idx as number | undefined) ?? 0) > 0;
  const details = import.meta.env.DEV
    ? [error?.stack || error?.message, componentStack]
        .filter(Boolean)
        .join("\n")
    : (error?.message ?? "");

  // A page-level error takes the whole view, so focus has to follow it.
  useEffect(() => {
    if (!inline) card.current?.focus();
  }, [inline]);

  return (
    <div
      className={cn(
        "flex w-full items-center justify-center px-4 py-10",
        inline ? "min-h-[240px]" : "min-h-screen bg-background",
      )}
    >
      <div
        ref={card}
        role="alert"
        tabIndex={inline ? undefined : -1}
        className={cn(
          "w-full rounded-2xl border bg-card shadow-sm outline-none",
          inline ? "max-w-sm p-6" : "max-w-md p-10",
        )}
      >
        <div className="flex flex-col items-center text-center">
          <div
            className={cn(
              "flex items-center justify-center rounded-2xl bg-destructive/10 ring-1 ring-inset ring-destructive/20",
              inline ? "h-12 w-12" : "h-14 w-14",
            )}
          >
            <TriangleAlert
              className={cn("text-destructive", inline ? "h-6 w-6" : "h-7 w-7")}
            />
          </div>

          <h2 className="mt-5 text-lg font-semibold tracking-tight text-foreground">
            Something went wrong
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-balance text-muted-foreground">
            This {inline ? "section" : "page"} ran into an unexpected error.
          </p>

          <div className="mt-6 flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-center">
            {canGoBack && (
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => window.history.back()}
              >
                <ArrowLeft />
                Go back
              </Button>
            )}
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => window.location.reload()}
            >
              <RefreshCw />
              Reload
            </Button>
          </div>

          {details && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-4 text-muted-foreground hover:text-foreground"
              aria-expanded={showDetails}
              aria-controls={DETAILS_PANEL_ID}
              onClick={() => setShowDetails((shown) => !shown)}
            >
              Details
              <ChevronDown
                className={cn(
                  "transition-transform",
                  showDetails && "rotate-180",
                )}
              />
            </Button>
          )}
        </div>

        {details && showDetails && (
          <div id={DETAILS_PANEL_ID} className="relative mt-3">
            <CopyDetailsButton details={details} />
            <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border bg-muted/50 p-3 pr-11 text-left font-mono text-[11px] leading-relaxed text-muted-foreground">
              {details}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

const COPY_FEEDBACK: Record<CopyStatus, { icon: LucideIcon; label: string }> = {
  idle: { icon: Copy, label: "Copy error details" },
  copied: { icon: Check, label: "Copied" },
  failed: { icon: X, label: "Couldn't copy — select the text instead" },
};

const CopyDetailsButton = ({ details }: { details: string }) => {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const resetTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  // Absent outside a secure context, and writeText rejects on a denied permission.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      setStatus("copied");
    } catch {
      setStatus("failed");
    }
    window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setStatus("idle"), 2000);
  };

  const { icon: Icon, label } = COPY_FEEDBACK[status];

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "absolute right-1 top-1 h-7 w-7 text-muted-foreground hover:text-foreground",
        status === "failed" && "text-destructive hover:text-destructive",
      )}
      aria-label={label}
      title={label}
      onClick={copy}
    >
      <Icon />
    </Button>
  );
};
